import { useMemo } from "react";
import { styled, css } from "styled-components";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";
import {
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { surfaceColorMap } from "../../util/styles.js";
import type { BackgroundColor } from "../../types/theme.js";

interface Graphic {
  image: Image;
  illustrationSvg: string;
}

export interface Props {
  title?: string;
  text?: string;
  tips?: string;
  backgroundColor?: BackgroundColor;
  graphic?: Graphic;
}

export const TextContent: React.FunctionComponent<Props> = ({
  text,
  tips,
  backgroundColor,
  graphic,
}) => {
  const unescapedIllustration = useMemo<string | undefined>(() => {
    if (!graphic) return undefined;

    if (graphic.illustrationSvg) {
      return graphic.illustrationSvg
        .replace(/__ss__/g, "<script>")
        .replace(/__se__/g, "</script>");
    }

    return undefined;
  }, [graphic?.illustrationSvg]);

  const content = (
    <HorizontalSpacer>
      <Container $hasTip={!!tips} $hasGraphic={!!graphic}>
        <div className="hide-mobile" />
        <RichText
          dangerouslySetInnerHTML={{ __html: text || "" }}
          {...createEditableProps("html", "text")}
        />
        <Sidebar
          className={tips || graphic ? "" : "hide-mobile"}
          $hasGraphic={!!graphic}
        >
          {tips && (
            <Tips $backgroundColor={backgroundColor}>
              <RichText
                dangerouslySetInnerHTML={{ __html: tips }}
                {...createEditableProps("html", "tips")}
              />
            </Tips>
          )}
          {graphic && (
            <Graphic>
              {unescapedIllustration && (
                <div
                  dangerouslySetInnerHTML={{ __html: unescapedIllustration }}
                />
              )}
              {graphic.image && (
                <img
                  src={transformImageUrl(graphic.image, { width: 650 })}
                  alt={graphic.image.altText}
                />
              )}
            </Graphic>
          )}
        </Sidebar>
      </Container>
    </HorizontalSpacer>
  );

  return (
    <VerticalSpacer>
      <Wrapper $backgroundColor={backgroundColor}>{content}</Wrapper>
    </VerticalSpacer>
  );
};

const Wrapper = styled.div<{ $backgroundColor?: BackgroundColor }>`
  width: 100%;
  ${(props) =>
    props.$backgroundColor &&
    css`
      background-color: ${surfaceColorMap[props.$backgroundColor]};
      color: ${props.$backgroundColor === "surface3" ? "#fff" : "var(--swatch2)"};
      padding: 1.5rem 0;
      padding-block: 1lh;
    `};
`;

const Container = styled.div<{ $hasTip: boolean; $hasGraphic: boolean }>`
  display: grid;
  margin: 0 auto;
  grid-template-columns: 1fr;
  max-width: 825px;
  gap: ${(props) => props.theme.spacing.l};

  > :first-child {
    display: none;
  }

  .hide-mobile {
    display: none;
  }

  @media (min-width: 1100px) {
    gap: ${(props) => props.theme.spacing.xl};
    max-width: 1100px;
    grid-template-columns: 1fr 825px 1fr;

    .hide-mobile {
      display: initial;
    }

    ${(props) =>
      (props.$hasTip || props.$hasGraphic) &&
      css`
        max-width: 825px;
        grid-template-columns: 1fr 250px;

        .hide-mobile {
          display: none;
        }
      `};
  }
`;

const Tips = styled.div<{ $backgroundColor?: BackgroundColor }>`
  padding: ${(props) => props.theme.spacing.l};
  border-radius: ${(props) => props.theme.borderRadius};

  background-color: ${(props) =>
    props.$backgroundColor ? "#fff" : "var(--swatch3)"};
  }};

  color: ${(props) =>
    props.$backgroundColor ? "var(--swatch2)" : "var(--swatch2)"};
  }};
`;

const Sidebar = styled.div<{ $hasGraphic: boolean }>`
  ${(props) =>
    props.$hasGraphic &&
    css`
      display: flex !important;
      flex-direction: column;
      gap: ${(props) => props.theme.spacing.l};
    `};
`;

const Graphic = styled.div`
  width: 100%;
  margin: auto;

  img,
  svg {
    width: 100%;
  }

  @media all and (min-width: 1100px) {
    width: 245px;

    img,
    svg {
      width: 245px;
    }
  }
`;
